import { OrderGrantedRefundStatusEnum } from "@dashboard/graphql";

export const REFUND_CONFIRMATION_POLL_INTERVAL_MS = 2000;
export const REFUND_CONFIRMATION_MAX_ATTEMPTS = 3;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface GrantedRefundLookupResult {
  data?: {
    order?: {
      grantedRefunds: Array<{ id: string; status: OrderGrantedRefundStatusEnum }>;
    } | null;
  };
}

/**
 * A granted refund tied to a payment transaction is confirmed asynchronously by
 * the gateway (e.g. PayPal) after the mutation that requested it already returned.
 * Poll briefly for the gateway's answer; if it hasn't resolved within the window,
 * the caller should treat it as still PENDING rather than assuming success.
 *
 * `isCancelled` is checked between attempts so a caller (e.g. a dialog that got
 * unmounted) can stop the loop instead of acting on a resolution nobody sees anymore.
 */
export const pollGrantedRefundStatus = async ({
  refundId,
  refetch,
  isCancelled,
}: {
  refundId: string;
  refetch: () => Promise<GrantedRefundLookupResult>;
  isCancelled: () => boolean;
}): Promise<OrderGrantedRefundStatusEnum> => {
  for (let attempt = 0; attempt < REFUND_CONFIRMATION_MAX_ATTEMPTS; attempt++) {
    const { data } = await refetch();

    if (isCancelled()) {
      return OrderGrantedRefundStatusEnum.PENDING;
    }

    const status = data?.order?.grantedRefunds.find(refund => refund.id === refundId)?.status;

    if (
      status === OrderGrantedRefundStatusEnum.SUCCESS ||
      status === OrderGrantedRefundStatusEnum.FAILURE
    ) {
      return status;
    }

    if (attempt < REFUND_CONFIRMATION_MAX_ATTEMPTS - 1) {
      await wait(REFUND_CONFIRMATION_POLL_INTERVAL_MS);
    }
  }

  return OrderGrantedRefundStatusEnum.PENDING;
};
